/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {CheckboxState} from '@ion/core/src/types/general';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import {IconCheckboxLabel} from '@ion/dls/src/components/checkbox/IconCheckboxLabel';
import {AutocompleteSearchResult} from '@ion/dls/src/components/search/AutocompleteSearchResult';
import {dlsColor} from '@ion/dls/src/theme/theme';

interface FilterSearchListItemProps {
  searchValue: string;
  clearSearchValue?: Function;
  onOptionSelected: Function;
  onGroupSelected: Function;
  itemId: string;
  item: any;
}

const useStyles = makeStyles(theme => ({
  checkboxItem : {
    paddingLeft : '11px',
    '& label'   : {
      width                    : '100%',
      '& .MuiTypography-body1' : {
        width : '100%'
      }
    }
  }
}));

/**
 * FilterSearchListItem is a child component used for FixedSizeList
 * of FilterSearchResults (asset filters and group filters)
 *
 * ```typescript
 *
 * import FilterSearchListItem from '@ion/dls/src/filters/filterSearchListItem';
 *
 * ...
 *
 * const renderList = ({index, style, data}) => {
 *
 *    const item = data[index];
 *
 *    return (
 *       <FilterSearchListItem
 *        item={item}
 *        searchValue={searchValue}
 *        clearSearchValue={clearSearchValue}
 *        onGroupSelected={onGroupSelected}
 *        onOptionSelected={onOptionSelected}/>
 *     )
 * }
 * ...
 *
 * <FixedSizeList
 *   itemData={data}
 *   height={150}
 *   itemCount={30}
 *   itemSize={5}>
 *   {renderList}
 * </FixedSizeList>
 *
 * ```
 */
const FilterSearchListItem = ({
  item,
  itemId,
  searchValue,
  clearSearchValue,
  onGroupSelected,
  onOptionSelected
}: FilterSearchListItemProps) => {
  const classes = useStyles();

  const onChange = useCallback((itemId: string) => {
    return function({target:{checked}}: ChangeEvent<HTMLInputElement>) {
      onOptionSelected(itemId, checked);
    };
  }, []);

  const onIconClick = useCallback((itemId: string) => {
    if (item?.childrenIds?.length) {
      return function(event) {
        event.stopPropagation();
        event.preventDefault();
        onGroupSelected(itemId);
        clearSearchValue();
      };
    }
  }, []);

  return (
    <div className={classes.checkboxItem}
      key={`filterSearch-item-${itemId}`}>
      <CheckBox checked={item.checked === CheckboxState.Checked}
        label={
          <IconCheckboxLabel label={
            <AutocompleteSearchResult option={{name: item.title}}
              searchValue={searchValue}
              highlightColor={dlsColor('lightBlue')} />
          } onIconClick={onIconClick(itemId)} />}
        onChange={onChange(itemId)}/>
    </div>
  );
};

export default FilterSearchListItem;
